// dokumen.view — Fase A hub "📄 Dokumen & Template" (Apo 20 Jul): SATU menu, tab menyamping,
// isi dipilih lewat DROPDOWN (bukan semua tertumpuk di satu halaman panjang).
//   📄 Dokumen  → pilih dokumen → S&K / teks-nya diedit DI SINI (menu S&K terpisah dihapus)
//   💬 Template → pilih pesan (Pelanggan/Internal/Supplier) → edit + chip placeholder + contoh hasil
//   🖨️ Cetak    → ukuran kertas (½A4 default) + uji cetak
// Tata letak 1 kolom, tombol besar → terbaca di HP. Editor tata-letak visual (band, seret-taruh)
// menyusul di Fase B; di sini yang bisa diubah dulu = ISI teks dokumen.
import { esc, wa62 } from '@domain/format';
import { toast } from '@ui/toast';
import {
  kebijakan, skTanda, skKhusus, skKuasa, skTracking, saveSetting, getSetting, fetchSetting, waTpl, profil,
  WA_TPL_META, DEFAULT_WA_TPL, type WaTpl,
} from '@core/settings';
import { simpanSelisih, berbedaDariBawaan } from '@domain/tpl-diff';
import { urutkanTata, semuaBlok, PREFIKS_TAMBAHAN,
  type SecDef, type TataSimpan, type Lebar, type Rata, type GayaBlok, type BlokTambahan } from '@domain/doclayout';
import { TANDA_SECTIONS, NOTA_SECTIONS, KUASA_SECTIONS, TRACK_SECTIONS, docTata } from '@modules/documents/documents.doc';
import { previewTandaTerima, previewNota, previewTracking, previewLabel } from '@modules/documents/documents.view';
import { waKantor, saveWaKantor, PERAN_WA, type WaKantor, type PeranWa, type NomorKantor } from '@core/wa-kantor';
import { listCabang } from '@modules/cabang/cabang.repo';

type Tab = 'dok' | 'wa' | 'nomor' | 'cetak';
let dkTab: Tab = 'dok';
let dkDoc = 'tanda';
let dkWa: keyof WaTpl = 'terima';

const dis = (can: boolean): string => (can ? '' : 'disabled');

/** Dokumen + bagian teks yang BISA diubah Apo hari ini (Fase A). `sk` = kunci setting daftar-baris. */
const DOKUMEN: { k: string; label: string; sk?: { key: string; judul: string; get: () => string[] }[]; catatan: string; blok?: SecDef[] }[] = [
  {
    k: 'tanda', label: '📄 Tanda Terima Servis', blok: TANDA_SECTIONS,
    catatan: 'Dicetak saat unit diterima. Dua tampilan: <b>normal</b> dan <b>case khusus</b> (unit tak bisa dites penuh) — S&K khusus di bawah hanya tercetak bila CS mencentang "case khusus" saat terima servis.',
    sk: [
      { key: 'sk_tanda', judul: 'S&K Tanda Terima (selalu tercetak)', get: skTanda },
      { key: 'sk_khusus', judul: 'S&K KHUSUS (hanya bila case khusus)', get: skKhusus },
    ],
  },
  { k: 'nota', label: '🧾 Nota Servis', blok: NOTA_SECTIONS, catatan: 'Dicetak saat unit diserahkan. Isi menyesuaikan otomatis: biaya simpan, riwayat servis-ulang, status LUNAS, QR survei, kartu garansi.' },
  {
    k: 'kuasa', label: '📝 Surat Kuasa / Diwakilkan', blok: KUASA_SECTIONS,
    catatan: 'Dicetak bila unit diambil orang lain. Memuat identitas pengambil + pernyataan pelepasan tuntutan + tanda tangan pengambil/CS/saksi. Salinan yang dikirim ke pelanggan lewat WA <b>tidak memuat foto, No. KTP, dan alamat</b>.',
    sk: [{ key: 'sk_kuasa', judul: 'S&K Surat Kuasa', get: skKuasa }],
  },
  {
    k: 'tracking', label: '🔗 Kartu Tracking', blok: TRACK_SECTIONS,
    catatan: 'Kartu status servis untuk pelanggan — <b>dikirim lewat WhatsApp, tidak dicetak</b> (keputusan Apo). Lebarnya dijaga seukuran kartu agar enak dibaca di HP.',
    sk: [{ key: 'sk_tracking', judul: 'S&K Kartu Tracking', get: skTracking }],
  },
  { k: 'label', label: '🏷️ Label QR Unit', catatan: 'Stiker QR yang ditempel di unit.' },
  { k: 'refund', label: '💸 Nota Pengembalian Dana', catatan: 'Dicetak saat pengembalian dana diproses.' },
  { k: 'slip', label: '💵 Slip Pembayaran Komisi', catatan: 'Dicetak saat Finance membayar komisi (berisi TTD penerima).' },
  { k: 'statement', label: '🏭 Statement Hutang Supplier', catatan: 'Kartu hutang per supplier (debit penyerahan / kredit pembayaran / saldo berjalan).' },
];

/** Diisi tab Nomor WA: true bila ada baris di layar yang belum disimpan. */
let wkKotor: (() => boolean) | null = null;
/** Diisi tab Dokumen: true bila susunan blok di layar belum disimpan. */
let blokKotor = false;
/** Satu pintu untuk semua perpindahan yang bisa membuang suntingan yang belum disimpan. */
function bolehTinggalkan(): boolean {
  if (wkKotor?.()) { if (!confirm('Ada nomor yang belum disimpan. Tinggalkan?')) return false; wkKotor = null; }
  if (blokKotor) { if (!confirm('Susunan blok belum disimpan. Tinggalkan?')) return false; blokKotor = false; }
  return true;
}

export function renderDokumenHub(body: HTMLElement, canEdit: boolean): void {
  const tabs: [Tab, string][] = [['dok', '📄 Dokumen'], ['wa', '💬 Template Pesan'], ['nomor', '📱 Nomor WA Kantor'], ['cetak', '🖨️ Cetak']];
  const bar = tabs.map(([k, l]) => `<button class="btn ${dkTab === k ? 'primary' : ''}" data-dk="${k}" style="padding:6px 12px;font-size:13px">${l}</button>`).join(' ');
  body.innerHTML = `<div style="display:flex;gap:6px;margin:0 0 12px;flex-wrap:wrap">${bar}</div><div id="dk_body"></div>`;
  body.querySelectorAll<HTMLElement>('button[data-dk]').forEach((b) => b.addEventListener('click', () => {
    // Suntingan yang hidup di DOM (nomor WA, susunan blok) jangan hilang diam-diam saat pindah.
    if (!bolehTinggalkan()) return;
    dkTab = (b.dataset['dk'] as Tab); renderDokumenHub(body, canEdit);
  }));
  const host = body.querySelector('#dk_body') as HTMLElement;
  if (dkTab === 'wa') waTab(host, canEdit, () => renderDokumenHub(body, canEdit));
  else if (dkTab === 'nomor') void nomorTab(host, canEdit, () => renderDokumenHub(body, canEdit));
  else if (dkTab === 'cetak') cetakTab(host, canEdit);
  else dokTab(host, canEdit, () => renderDokumenHub(body, canEdit));
}

// ── 📄 Dokumen ──────────────────────────────────────────────────────────────
function dokTab(host: HTMLElement, canEdit: boolean, refresh: () => void): void {
  const d = DOKUMEN.find((x) => x.k === dkDoc) ?? DOKUMEN[0]!;
  const opsi = DOKUMEN.map((x) => `<option value="${x.k}" ${x.k === dkDoc ? 'selected' : ''}>${esc(x.label)}</option>`).join('');
  const blokSK = (d.sk ?? []).map((s) => `
    <div class="field" style="margin-top:10px"><label>${esc(s.judul)} <span style="color:var(--muted);font-weight:400">— satu poin per baris</span></label>
      <textarea class="dk_sk" data-key="${esc(s.key)}" rows="7" ${dis(canEdit)} style="width:100%;font-size:12.5px">${esc(s.get().join('\n'))}</textarea></div>`).join('');

  const tata = docTata(d.k);
  const urutTampil: SecDef[] = d.blok ? urutkanTata(semuaBlok(d.blok, tata), tata) : [];
  const barisBlok = urutTampil.map((b, i) => {
    const sembunyi = (tata.sembunyi ?? []).includes(b.id);
    const lebar = tata.lebar?.[b.id] ?? b.width ?? 'full';   // hormati lebar BAWAAN blok, jangan diam-diam jadi penuh
    const rata = tata.rata?.[b.id] ?? '';
    const g = tata.gaya?.[b.id] ?? {};   // kosong = ikut bawaan dokumen
    return `<div class="dk-blok" data-id="${esc(b.id)}" style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;padding:6px;border:1px solid var(--line);border-radius:8px;margin:5px 0;${sembunyi ? 'opacity:.55' : ''}">
      <span style="display:flex;gap:2px">
        <button class="ico-btn dk-up" ${i === 0 || !canEdit ? 'disabled' : ''} title="Naikkan" aria-label="Naikkan ${esc(b.label)}">▲</button>
        <button class="ico-btn dk-down" ${i === urutTampil.length - 1 || !canEdit ? 'disabled' : ''} title="Turunkan" aria-label="Turunkan ${esc(b.label)}">▼</button>
      </span>
      <span style="flex:1;min-width:150px;font-size:13px">${esc(b.label)}${b.wajib ? ' <span class="pill" style="background:var(--ice);color:var(--navy)">wajib</span>' : ''}${b.id.startsWith(PREFIKS_TAMBAHAN) ? ' <span class="pill" style="background:var(--okbg);color:var(--ok)">blok Anda</span>' : ''}</span>
      ${b.id.startsWith(PREFIKS_TAMBAHAN) && canEdit ? `<button class="ico-btn dk-hapusblok" data-id="${esc(b.id)}" title="Hapus blok ini" style="border-color:var(--danger);color:var(--danger)">🗑️</button>` : ''}
      <label style="font-size:12px;display:flex;align-items:center;gap:4px">
        <input type="checkbox" class="dk-show" ${sembunyi ? '' : 'checked'} ${b.wajib || !canEdit ? 'disabled' : ''}> tampil</label>
      <select class="dk-lebar" ${dis(canEdit)} style="font-size:12px">
        <option value="full" ${lebar === 'full' ? 'selected' : ''}>Lebar penuh</option>
        <option value="half" ${lebar === 'half' ? 'selected' : ''}>½ (berdampingan)</option>
        <option value="third" ${lebar === 'third' ? 'selected' : ''}>⅓ (tiga sebaris)</option></select>
      <select class="dk-rata" ${dis(canEdit)} style="font-size:12px">
        <option value="" ${rata === '' ? 'selected' : ''}>Rata bawaan</option>
        <option value="left" ${rata === 'left' ? 'selected' : ''}>Kiri</option>
        <option value="center" ${rata === 'center' ? 'selected' : ''}>Tengah</option>
        <option value="right" ${rata === 'right' ? 'selected' : ''}>Kanan</option></select>
      ${b.teks != null ? `<div style="flex-basis:100%">
        <div style="font-size:11.5px;color:var(--muted);display:flex;gap:6px;align-items:center">
          <span>Teks blok ${tata.teks?.[b.id] != null ? '<b style="color:var(--warn)">· diubah dari bawaan</b>' : '· masih teks bawaan'}</span>
          ${tata.teks?.[b.id] != null && canEdit ? `<button type="button" class="btn dk-teksreset" data-id="${esc(b.id)}" style="padding:1px 8px;font-size:11px">↺ bawaan</button>` : ''}
        </div>
        <textarea class="dk-teks" rows="${b.teks.length > 120 ? 5 : 2}" ${dis(canEdit)} placeholder="Teks blok ini" style="width:100%;font-size:12.5px">${esc(tata.teks?.[b.id] ?? b.teks)}</textarea>
        ${b.id === 'pernyataan' ? '<div style="font-size:11px;color:var(--muted)">Tulis <b>{pengambil}</b> di tempat nama pengambil akan muncul (boleh lebih dari sekali).</div>' : ''}
        ${b.wajib ? '<div style="font-size:11px;color:var(--muted)">Dikosongkan = kembali ke teks bawaan. Untuk menghilangkan blok, hilangkan centang <b>tampil</b>.</div>' : ''}
      </div>` : ''}
      ${b.isiDari ? `<div style="flex-basis:100%;font-size:11.5px;color:var(--muted)">Isi otomatis — ${esc(b.isiDari)}</div>` : ''}
      <div style="flex-basis:100%;display:flex;gap:8px;align-items:center;font-size:11.5px;color:var(--muted)">
        <span>Jarak atas <input class="dk-mt" type="number" min="0" max="40" step="1" value="${g.mt ?? ''}" placeholder="—" ${dis(canEdit)} style="width:52px;text-align:right"> mm</span>
        <span>bawah <input class="dk-mb" type="number" min="0" max="40" step="1" value="${g.mb ?? ''}" placeholder="—" ${dis(canEdit)} style="width:52px;text-align:right"> mm</span>
        <span>huruf <input class="dk-fs" type="number" min="6" max="30" step="1" value="${g.fs ?? ''}" placeholder="—" ${dis(canEdit)} style="width:52px;text-align:right"> px</span>
      </div>
    </div>`;
  }).join('');

  // Susunan yang disimpan SEBELUM kertas melebar tak punya info lebar sama sekali → semua blok
  // jadi penuh dan dokumen kembali 2 lembar. Apo tak akan menduga harus menekan ↺.
  const adaBawaanHalf = (d.blok ?? []).some((b) => b.width === 'half');
  const tataUsang = adaBawaanHalf && (tata.urut?.length ?? 0) > 0 && !tata.lebar;
  const kertasDok = tata.kertas ?? '';
  const panelBlok = d.blok ? `
    <div class="field" style="max-width:420px;margin-top:12px"><label>Ukuran kertas dokumen ini</label>
      <select id="dk_kertas" ${dis(canEdit)} style="font-size:13px">
        <option value="" ${kertasDok === '' ? 'selected' : ''}>Ikut setelan umum (${esc(kebijakan().printSize === 'A4' ? 'A4 penuh' : '½ A4 melebar')})</option>
        <option value="A5" ${kertasDok === 'A5' ? 'selected' : ''}>½ A4 melebar — 210 × 148 mm</option>
        <option value="A4" ${kertasDok === 'A4' ? 'selected' : ''}>A4 penuh — 210 × 297 mm</option></select>
      <div style="font-size:11px;color:var(--muted);margin-top:3px">Dokumen padat teks (mis. Surat Kuasa) lebih pas di A4; tanda terima & nota muat di ½ A4.</div></div>
    <div class="field" style="max-width:420px"><label>Bantalan tepi kiri-kanan (mm)</label>
      <input id="dk_margin" type="number" min="5" max="30" step="1" value="${tata.marginMm ?? ''}" placeholder="ikut umum (12)" ${dis(canEdit)} style="width:130px;text-align:right">
      <div style="font-size:11px;color:var(--muted);margin-top:3px">Perbesar bila printer memotong tepi; 5–30 mm. Kosong = ikut setelan umum.</div></div>
    ${tataUsang ? '<div class="scope-note" style="border-left:3px solid var(--warn);background:var(--warnbg)">⚠️ Susunan tersimpan Anda dibuat sebelum kertas <b>½ A4 melebar</b> dipakai, sehingga semua blok tercetak menumpuk ke bawah (bisa jadi 2 lembar). Tekan <b>↺ Kembalikan ke bawaan</b> untuk memakai susunan baru yang muat 1 lembar.</div>' : ''}
    <div class="ttl" style="font-size:15px;margin:14px 0 4px">🧱 Susunan Blok</div>
    <div class="scope-note">Urutkan dengan ▲▼, hilangkan centang untuk menyembunyikan, atau setel dua blok berurutan jadi <b>½</b> supaya tampil berdampingan. Blok bertanda <b>wajib</b> tak bisa disembunyikan. Baris <b>jarak & huruf</b> di bawah tiap blok untuk menyetel halus — kosongkan berarti ikut bawaan. Setelah <b>💾 Simpan</b>, klik <b>👁️ Lihat contoh cetakan</b> untuk memeriksa hasilnya (contoh memakai susunan yang <b>sudah tersimpan</b>).</div>
    ${barisBlok}
    ${canEdit ? '<button class="btn" id="dk_tambahblok" style="padding:6px 12px;margin-top:6px">➕ Tambah blok teks</button><div class="hint" style="margin-top:4px">Isi blok ikut tercetak dan ikut terkirim ke pelanggan — jangan taruh catatan internal (harga modal, instruksi teknisi) di sini.</div>' : ''}
    ${canEdit ? '<div style="display:flex;gap:8px;margin-top:8px"><button class="btn primary" id="dk_saveblok" style="padding:7px 14px">💾 Simpan Susunan</button><button class="btn" id="dk_resetblok" style="padding:7px 14px">↺ Kembalikan ke bawaan</button></div>' : ''}`
    : '<div class="scope-note" style="font-size:12px">Susunan blok untuk dokumen ini menyusul — sekarang baru <b>Tanda Terima Servis</b> yang bisa diatur.</div>';

  host.innerHTML = `
    <div class="field" style="max-width:420px"><label>Pilih dokumen</label><select id="dk_pil" style="font-size:13px">${opsi}</select></div>
    <div class="scope-note">${d.catatan}</div>
    ${blokSK}
    ${panelBlok}
    <div style="display:flex;gap:8px;margin-top:12px;flex-wrap:wrap">
      ${d.sk && canEdit ? '<button class="btn primary" id="dk_save" style="padding:8px 16px">💾 Simpan</button>' : ''}
      <button class="btn" id="dk_prev" style="padding:8px 16px">👁️ Lihat contoh cetakan</button>
    </div>`;

  const pil = host.querySelector('#dk_pil') as HTMLSelectElement;
  pil.addEventListener('change', () => {
    if (!bolehTinggalkan()) { pil.value = dkDoc; return; }   // batal → kembalikan pilihannya
    dkDoc = pil.value; refresh();
  });
  host.querySelector('#dk_save')?.addEventListener('click', async () => {
    try {
      for (const el of host.querySelectorAll<HTMLTextAreaElement>('textarea.dk_sk')) {
        const lines = el.value.split('\n').map((x) => x.trim()).filter(Boolean);
        await saveSetting(el.dataset['key'] ?? '', lines);
      }
      toast('✅ Tersimpan');
    } catch (e) { toast((e as Error).message); }
  });
  host.querySelector('#dk_prev')?.addEventListener('click', () => void contohCetakan(d.k));

  // ── Susunan blok ──────────────────────────────────────────────────────────
  // Urutan hidup di DOM (▲▼ menukar baris) dan baru ditulis ke server saat Simpan — sama
  // seperti tab Nomor WA, supaya Apo bisa mencoba beberapa susunan tanpa menyimpan setengah jadi.
  const tukar = (el: HTMLElement, arah: -1 | 1): void => {
    const box = el.parentElement; if (!box) return;
    const lain = arah === -1 ? el.previousElementSibling : el.nextElementSibling;
    if (!lain || !lain.classList.contains('dk-blok')) return;
    if (arah === -1) box.insertBefore(el, lain); else box.insertBefore(lain, el);
    // Tombol ▲ pertama & ▼ terakhir dimatikan ulang agar tak bisa keluar batas.
    box.querySelectorAll<HTMLElement>('.dk-blok').forEach((r, i, all) => {
      (r.querySelector('.dk-up') as HTMLButtonElement | null)?.toggleAttribute('disabled', i === 0);
      (r.querySelector('.dk-down') as HTMLButtonElement | null)?.toggleAttribute('disabled', i === all.length - 1);
    });
  };
  blokKotor = false;
  const tandaiKotor = (): void => { blokKotor = true; };
  host.querySelectorAll<HTMLElement>('.dk-up').forEach((b) => b.addEventListener('click', () => { tukar(b.closest('.dk-blok') as HTMLElement, -1); tandaiKotor(); }));
  host.querySelectorAll<HTMLElement>('.dk-down').forEach((b) => b.addEventListener('click', () => { tukar(b.closest('.dk-blok') as HTMLElement, 1); tandaiKotor(); }));
  host.querySelectorAll<HTMLElement>('.dk-show, .dk-lebar, .dk-rata, .dk-mt, .dk-mb, .dk-fs, .dk-teks, #dk_kertas, #dk_margin').forEach((el) => el.addEventListener('change', tandaiKotor));
  // Kembalikan teks satu blok ke bawaan — tanpa menghapus urutan/lebar/gaya seluruh dokumen.
  host.querySelectorAll<HTMLElement>('.dk-teksreset').forEach((b) => b.addEventListener('click', () => {
    const baris = b.closest('.dk-blok') as HTMLElement | null;
    const ta = baris?.querySelector('.dk-teks') as HTMLTextAreaElement | null;
    const bawaan = d.blok?.find((x) => x.id === b.dataset['id'])?.teks ?? '';
    if (ta) { ta.value = bawaan; tandaiKotor(); toast('Teks kembali ke bawaan — tekan 💾 Simpan Susunan.'); }
  }));

  host.querySelector('#dk_tambahblok')?.addEventListener('click', async () => {
    // Menambah blok menulis langsung ke server dan me-refresh panel, jadi suntingan yang belum
    // ditekan 💾 (urutan, teks, lebar) akan hilang. Tanyakan dulu — tanpa ini hilangnya diam-diam.
    if (!bolehTinggalkan()) return;
    const label = prompt('Nama blok (untuk Anda kenali di daftar ini):', 'Catatan tambahan');
    if (label == null) return;
    // Blok baru langsung disimpan supaya punya tempat; teksnya diisi di kotak yang muncul.
    const nomor = (tata.tambahan?.length ?? 0) + 1;
    const baru: BlokTambahan = { id: `${PREFIKS_TAMBAHAN}${nomor}_${Date.now().toString(36)}`, label: label.trim() || `Blok teks ${nomor}`, teks: '' };
    try {
      const segar = await fetchSetting<Record<string, TataSimpan>>('doc_tata', {});
      const t0 = segar[d.k] ?? {};
      await saveSetting('doc_tata', { ...segar, [d.k]: { ...t0, tambahan: [...(t0.tambahan ?? []), baru] } });
      blokKotor = false; toast('➕ Blok ditambahkan — isi teksnya lalu 💾 Simpan Susunan'); refresh();
    } catch (e) { toast((e as Error).message); }
  });
  host.querySelectorAll<HTMLElement>('.dk-hapusblok').forEach((b) => b.addEventListener('click', async () => {
    if (!bolehTinggalkan()) return;
    if (!confirm('Hapus blok teks ini dari dokumen?')) return;
    try {
      const segar = await fetchSetting<Record<string, TataSimpan>>('doc_tata', {});
      const t0 = segar[d.k] ?? {};
      const sisa = (t0.tambahan ?? []).filter((x) => x.id !== b.dataset['id']);
      await saveSetting('doc_tata', { ...segar, [d.k]: { ...t0, tambahan: sisa } });
      blokKotor = false; toast('🗑️ Blok dihapus'); refresh();
    } catch (e) { toast((e as Error).message); }
  }));

  host.querySelector('#dk_saveblok')?.addEventListener('click', async () => {
    const urut: string[] = [], sembunyi: string[] = [];
    const lebar: Record<string, Lebar> = {}, rata: Record<string, Rata> = {};
    const gaya: Record<string, GayaBlok> = {};
    const teks: Record<string, string> = {};
    let batalKarenaPlaceholder = false;
    const isiTambahanBaru: Record<string, string> = {};
    host.querySelectorAll<HTMLElement>('.dk-blok').forEach((row) => {
      const id = row.dataset['id'] ?? ''; if (!id) return;
      urut.push(id);
      if (!(row.querySelector('.dk-show') as HTMLInputElement | null)?.checked) sembunyi.push(id);
      const lb = (row.querySelector('.dk-lebar') as HTMLSelectElement | null)?.value;
      // Disimpan bila BEDA dari bawaan blok itu — bukan sekadar "bukan full". Sejak blok
      // data/tracking/sk/ttd bawaannya ½, memilih "Lebar penuh" harus ikut tersimpan; kalau
      // tidak, pilihan Apo dibuang diam-diam dan tampilannya kembali berdampingan.
      const bawaanLebar = d.blok?.find((x) => x.id === id)?.width ?? 'full';
      if (lb && lb !== bawaanLebar) lebar[id] = lb as Lebar;
      const rt = (row.querySelector('.dk-rata') as HTMLSelectElement | null)?.value;
      if (rt) rata[id] = rt as Rata;
      // Kosong = ikut bawaan; angka (termasuk 0) = setelan Apo.
      const ang = (sel: string): number | undefined => {
        const v = (row.querySelector(sel) as HTMLInputElement | null)?.value.trim();
        return v ? Number(v) : undefined;
      };
      const gy: GayaBlok = {};
      const mt = ang('.dk-mt'), mb = ang('.dk-mb'), fs = ang('.dk-fs');
      if (mt != null && Number.isFinite(mt)) gy.mt = mt;
      if (mb != null && Number.isFinite(mb)) gy.mb = mb;
      if (fs != null && Number.isFinite(fs)) gy.fs = fs;
      if (Object.keys(gy).length) gaya[id] = gy;
      // Teks disimpan HANYA bila diubah — sama seperti template pesan, supaya perbaikan teks
      // bawaan berikutnya tetap sampai ke toko untuk blok yang tak Apo sentuh.
      const ta = row.querySelector('.dk-teks') as HTMLTextAreaElement | null;
      if (ta && id.startsWith(PREFIKS_TAMBAHAN)) { isiTambahanBaru[id] = ta.value; }
      else if (ta) {
        // Placeholder yang dihapus tak memunculkan error apa pun — namanya hanya hilang diam-diam
        // dari klausul hukum. Diperingatkan, sama seperti pada template pesan.
        if (id === 'pernyataan' && !ta.value.includes('{pengambil}')) batalKarenaPlaceholder = true;
        const bawaanTeks = d.blok?.find((x) => x.id === id)?.teks ?? '';
        if (ta.value.trim() && ta.value.trim() !== bawaanTeks.trim()) teks[id] = ta.value.trim();
      }
    });
    if (batalKarenaPlaceholder && !confirm('Teks pernyataan tidak memuat {pengambil} — nama pengambil TIDAK akan tercetak di klausul itu.\n\nTetap simpan?')) return;
    const kertasPilih = (host.querySelector('#dk_kertas') as HTMLSelectElement | null)?.value;
    const mv = (host.querySelector('#dk_margin') as HTMLInputElement | null)?.value.trim();
    const marginPilih = mv ? Number(mv) : undefined;
    const tataBaru: TataSimpan = { urut, ...(sembunyi.length ? { sembunyi } : {}), lebar,
      ...(Object.keys(rata).length ? { rata } : {}), ...(Object.keys(gaya).length ? { gaya } : {}),
      ...(Object.keys(teks).length ? { teks } : {}),
      ...(tata.tambahan?.length ? { tambahan: tata.tambahan.map((t) => ({ ...t, teks: isiTambahanBaru[t.id] ?? t.teks })) } : {}),
      ...(marginPilih != null && Number.isFinite(marginPilih) ? { marginMm: marginPilih } : {}),
      ...(kertasPilih ? { kertas: kertasPilih as 'A5' | 'A4' } : {}) };
    try {
      const segar = await fetchSetting<Record<string, TataSimpan>>('doc_tata', {});   // jangan timpa dokumen lain
      await saveSetting('doc_tata', { ...segar, [d.k]: tataBaru });
      blokKotor = false; toast('✅ Susunan disimpan'); refresh();
    } catch (e) { toast((e as Error).message); }
  });

  host.querySelector('#dk_resetblok')?.addEventListener('click', async () => {
    if (!confirm(`Kembalikan susunan "${d.label}" ke bawaan? Pengaturan Anda pada dokumen ini akan hilang.`)) return;
    try {
      const segar = await fetchSetting<Record<string, TataSimpan>>('doc_tata', {});
      const next = { ...segar }; delete next[d.k];
      await saveSetting('doc_tata', next);
      blokKotor = false; toast('↺ Kembali ke susunan bawaan'); refresh();
    } catch (e) { toast((e as Error).message); }
  });
}

/** Contoh cetakan: pakai tiket NYATA terbaru supaya Apo melihat hasil sebenarnya, bukan data karangan. */
async function contohCetakan(kind: string): Promise<void> {
  try {
    const { supabase } = await import('@core/supabase');
    const { data } = await supabase().from('tickets').select('no').order('tgl_iso', { ascending: false }).limit(1).maybeSingle();
    const no = (data as { no: string } | null)?.no;
    if (!no) { toast('Belum ada tiket untuk dijadikan contoh.'); return; }
    if (kind === 'nota') await previewNota(no);
    else if (kind === 'tracking') await previewTracking(no);
    else if (kind === 'label') await previewLabel(no);
    else if (kind === 'tanda') await previewTandaTerima(no);
    else if (kind === 'kuasa') {
      // Contoh dengan data KARANGAN. Membuka Surat Kuasa dari alur normal mewajibkan isi
      // identitas + foto lalu MENULIS ke tiket & penyimpanan — jangan sampai Apo harus
      // meninggalkan jejak permanen pada tiket nyata hanya untuk memeriksa susunan blok.
      const { getDocMeta } = await import('@modules/documents/documents.repo');
      const { suratKuasaHTML } = await import('@modules/documents/documents.doc');
      const { previewDoc } = await import('@modules/documents/documents.view');
      const meta = await getDocMeta(no);
      previewDoc('Contoh Surat Kuasa', '📝', suratKuasaHTML(meta,
        { nama: '(nama pengambil)', ktp: '(no. KTP)', alamat: '(alamat KTP)', hp: '(no. HP)', hubungan: '(hubungan)' },
        '(nama CS)', null), undefined, { kertas: docTata('kuasa').kertas, marginMm: docTata('kuasa').marginMm });
    } else toast('Contoh untuk dokumen ini dibuka dari menu terkait (dokumen dibuat saat transaksinya berjalan).');
  } catch (e) { toast((e as Error).message); }
}

// ── 💬 Template Pesan ───────────────────────────────────────────────────────
function waTab(host: HTMLElement, canEdit: boolean, refresh: () => void): void {
  const tpl = waTpl();
  // Yang benar-benar tersimpan (selisih dari bawaan) — hanya untuk penanda "diubah/masih bawaan".
  // Saat MENYIMPAN dipakai nilai segar dari DB, bukan ini (lihat dk_wasave).
  const tersimpan = getSetting<Record<string, string>>('wa_tpl', {});
  const meta = WA_TPL_META.find((m) => m.key === dkWa) ?? WA_TPL_META[0]!;
  const grup = [...new Set(WA_TPL_META.map((m) => m.grup))];
  const opsi = grup.map((g) => `<optgroup label="${esc(g)}">${WA_TPL_META.filter((m) => m.grup === g)
    .map((m) => `<option value="${m.key}" ${m.key === dkWa ? 'selected' : ''}>${esc(m.label)}</option>`).join('')}</optgroup>`).join('');
  const diubah = berbedaDariBawaan(tersimpan, DEFAULT_WA_TPL as unknown as Record<string, string>, meta.key);
  const chips = meta.ph.map((p) => `<button class="btn dk_chip" data-ph="{${p}}" style="padding:2px 8px;font-size:11.5px">{${p}}</button>`).join(' ');
  // Contoh hasil: isi placeholder dgn nilai masuk akal supaya Apo tahu bentuk akhirnya.
  const contoh: Record<string, string> = {
    nama: 'Budi', no: 'PKP-260720-A1B2', tracking: 'https://ifics.app/t/abc123', est: 'Rp 300.000',
    lama: ' · lama 2 hari', ket: 'LCD retak', total: 'Rp 350.000', sisa: 'Rp 150.000', jt: ' (jatuh tempo 3 Agu)',
    unit: 'Samsung A54', merk: 'Samsung', tipe: 'A54', kerusakan: 'Mati total',
    jumlah: 'Rp 200.000', via: 'Tunai', link: 'https://ifics.app/s/xyz',
    user: 'budi@ifics.local', pass: 'A1b2C3d4', pesan: 'unit menunggu diambil 12 hari',
  };
  // Placeholder tak dikenal → KOSONG, sama persis dengan `fillTpl` saat mengirim. Kalau di sini
  // ditampilkan `{xxx}` literal, Apo mengira bagian itu ikut terkirim padahal hilang.
  const isiContoh = (t: string): string => t.replace(/\{(\w+)\}/g, (_, k: string) => contoh[k] ?? '');

  host.innerHTML = `
    <div class="field" style="max-width:420px"><label>Pilih pesan</label><select id="dk_wapil" style="font-size:13px">${opsi}</select></div>
    <div class="scope-note">Yang di dalam <code>{kurung}</code> diisi otomatis saat pesan dikirim. Klik chip untuk menyisipkan.</div>
    <div style="display:flex;gap:5px;flex-wrap:wrap;margin:6px 0">${chips}</div>
    <div class="field"><label>Isi pesan ${diubah ? '<span style="font-weight:400;color:var(--warn)">· diubah dari bawaan</span>' : '<span style="font-weight:400;color:var(--muted)">· masih teks bawaan</span>'}</label><textarea id="dk_watxt" rows="5" ${dis(canEdit)} style="width:100%;font-size:13px">${esc(tpl[meta.key])}</textarea></div>
    <div class="field"><label>Contoh hasil kiriman</label>
      <div id="dk_wapv" style="background:#dcf8c6;border-radius:10px;padding:9px 12px;font-size:13px;white-space:pre-wrap;line-height:1.45;max-width:420px">${esc(isiContoh(tpl[meta.key]))}</div></div>
    <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:8px">
      ${canEdit ? '<button class="btn primary" id="dk_wasave" style="padding:8px 16px">💾 Simpan Pesan</button>' : ''}
      ${canEdit && diubah ? '<button class="btn" id="dk_wareset" style="padding:8px 16px">↺ Kembalikan ke bawaan</button>' : ''}
    </div>`;

  const ta = host.querySelector('#dk_watxt') as HTMLTextAreaElement;
  const pv = host.querySelector('#dk_wapv') as HTMLElement;
  ta.addEventListener('input', () => { pv.textContent = isiContoh(ta.value); });
  (host.querySelector('#dk_wapil') as HTMLSelectElement).addEventListener('change', (e) => { dkWa = (e.target as HTMLSelectElement).value as keyof WaTpl; refresh(); });
  host.querySelectorAll<HTMLElement>('button.dk_chip').forEach((b) => b.addEventListener('click', () => {
    const ph = b.dataset['ph'] ?? '';
    const i = ta.selectionStart ?? ta.value.length;
    ta.value = ta.value.slice(0, i) + ph + ta.value.slice(ta.selectionEnd ?? i);
    ta.focus(); ta.selectionStart = ta.selectionEnd = i + ph.length;
    pv.textContent = isiContoh(ta.value);
  }));
  host.querySelector('#dk_wasave')?.addEventListener('click', async () => {
    // Placeholder yang dihapus TIDAK memunculkan error apa pun saat kirim — bagiannya hanya
    // hilang diam-diam. Paling berbahaya di pesan akun karyawan: {pass} hilang = karyawan tak
    // pernah menerima passwordnya dan tak ada yang tahu kenapa. Karena itu diperingatkan di sini.
    const hilang = meta.ph.filter((p) => !ta.value.includes(`{${p}}`));
    if (hilang.length && !confirm(`Bagian ${hilang.map((p) => '{' + p + '}').join(', ')} tidak ada di pesan ini — isinya TIDAK akan ikut terkirim.\n\nTetap simpan?`)) return;
    try {
      const segar = await fetchSetting<Record<string, string>>('wa_tpl', {});   // jangan pakai cache boot: bisa menimpa suntingan orang lain
      await saveSetting('wa_tpl', simpanSelisih(segar, DEFAULT_WA_TPL as unknown as Record<string, string>, meta.key, ta.value));
      toast('✅ Pesan disimpan'); refresh();
    } catch (e) { toast((e as Error).message); }
  });
  host.querySelector('#dk_wareset')?.addEventListener('click', async () => {
    if (!confirm(`Kembalikan "${meta.label}" ke teks bawaan? Tulisan Anda pada pesan ini akan hilang.`)) return;
    try {
      const segar = await fetchSetting<Record<string, string>>('wa_tpl', {});
      const next = { ...segar }; delete next[meta.key];
      await saveSetting('wa_tpl', next); toast('↺ Kembali ke teks bawaan'); refresh();
    } catch (e) { toast((e as Error).message); }
  });
}

// ── 🖨️ Cetak ────────────────────────────────────────────────────────────────
function cetakTab(host: HTMLElement, canEdit: boolean): void {
  const k = kebijakan();
  host.innerHTML = `
    <div class="field" style="max-width:340px"><label>Ukuran kertas</label>
      <select id="ct_size" ${dis(canEdit)} style="font-size:13px">
        <option value="A5" ${k.printSize === 'A5' ? 'selected' : ''}>½ A4 melebar — 210 × 148 mm (default)</option>
        <option value="A4" ${k.printSize === 'A4' ? 'selected' : ''}>A4 penuh — 210 × 297 mm</option></select></div>
    <div class="scope-note"><b>½ A4</b> = selembar A4 dipotong <b>melintang</b> (melebar), bukan A5 tegak — sesuai kertas yang dipakai di toko. Bila isi dokumen tak muat, ganti ke <b>A4 penuh</b>. Berlaku untuk semua dokumen. Saat pratinjau, sistem memberi tahu <b>berapa lembar</b> yang akan tercetak. Bila lebih dari satu lembar, <b>tiap lembar dicap nomor dokumennya</b> supaya lembar kedua tak tertukar. Nomor halaman ("1 dari 2") bisa diaktifkan lewat pilihan <b>Header dan footer</b> di dialog cetak browser.</div>
    <div class="scope-note" style="font-size:12px">Kop dokumen memakai <b>${esc(profil().namaUsaha)}</b> — ubah nama/alamat/telepon di tab <b>🏪 Profil Toko</b>.</div>
    ${canEdit ? '<button class="btn primary" id="ct_save" style="padding:8px 16px;margin-top:6px">💾 Simpan</button>' : ''}`;
  host.querySelector('#ct_save')?.addEventListener('click', async () => {
    const size = (host.querySelector('#ct_size') as HTMLSelectElement).value === 'A4' ? 'A4' : 'A5';
    try { await saveSetting('kebijakan', { ...kebijakan(), printSize: size }); toast('✅ Ukuran cetak disimpan'); }
    catch (e) { toast((e as Error).message); }
  });
}

// ── 📱 Nomor WA Kantor ──────────────────────────────────────────────────────
// Tujuan pesan internal = NOMOR KANTOR per cabang (bukan nomor pribadi staf — keputusan Apo).
// Boleh lebih dari satu per peran; saat mengirim, staf memilih tujuannya.
function barisHTML(contoh: string, canEdit: boolean, n?: NomorKantor): string {
  return `<div class="wk_baris" style="display:flex;gap:6px;margin:4px 0;flex-wrap:wrap">
    <input class="wk_lbl" value="${esc(n?.label ?? '')}" placeholder="${esc(contoh)}" ${dis(canEdit)} style="flex:1;min-width:130px;font-size:12.5px">
    <input class="wk_no" value="${esc(n?.wa ?? '')}" placeholder="6281…" inputmode="numeric" ${dis(canEdit)} style="flex:1;min-width:130px;font-size:12.5px">
    ${canEdit ? '<button class="btn wk_hapus" style="padding:3px 9px;border-color:var(--danger);color:var(--danger)" title="Hapus nomor" aria-label="Hapus nomor">🗑️</button>' : ''}
  </div>`;
}

async function nomorTab(host: HTMLElement, canEdit: boolean, refresh: () => void): Promise<void> {
  host.innerHTML = '<div class="scope-note">Memuat cabang…</div>';
  let cabang: { kode: string; nama: string }[] = [];
  try { cabang = await listCabang(); } catch (e) { host.innerHTML = `<div class="panel err">${esc((e as Error).message)}</div>`; return; }
  const data = waKantor();

  const blokCabang = (c: { kode: string; nama: string }): string => `
    <div class="panel" style="padding:10px;margin-bottom:12px;border:1px solid var(--line);border-radius:8px">
      <div style="font-weight:600;color:var(--navy);margin-bottom:6px">🏢 ${esc(c.nama)} <span style="font-size:11px;color:var(--muted)">(${esc(c.kode)})</span></div>
      ${PERAN_WA.map((p) => {
        const list = data[c.kode]?.[p.k] ?? [];
        const baris = list.map((n) => barisHTML(p.contoh, canEdit, n)).join('');
        return `<div style="margin:8px 0 4px"><div style="font-size:12.5px;font-weight:600">${p.label}</div>
          <div class="wk_rows" data-c="${esc(c.kode)}" data-p="${p.k}">${baris}</div>
          <div class="wk_kosong" data-c="${esc(c.kode)}" data-p="${p.k}" style="font-size:12px;color:var(--muted);margin:3px 0;${list.length ? 'display:none' : ''}">Belum ada nomor — pesan internal ke peran ini belum bisa dikirim.</div>
          ${canEdit ? `<button class="btn wk_tambah" data-c="${esc(c.kode)}" data-p="${p.k}" style="padding:3px 10px;font-size:12px;margin-top:2px">➕ Tambah nomor</button>` : ''}</div>`;
      }).join('')}
    </div>`;

  host.innerHTML = `
    <div class="scope-note">Nomor tujuan <b>pesan internal</b> (bukan ke pelanggan). Pakai <b>nomor kantor</b> — bukan nomor pribadi staf — supaya tetap benar walau orangnya berganti. Boleh lebih dari satu per peran; saat mengirim, staf memilih tujuannya. <b>Supplier</b> tak diatur di sini — nomornya diambil dari data supplier masing-masing.</div>
    ${cabang.map(blokCabang).join('')}
    ${canEdit ? '<button class="btn primary" id="wk_save" style="padding:8px 16px">💾 Simpan Nomor</button>' : ''}`;

  // Baris hidup di DOM; DB ditulis HANYA saat Simpan. (Dulu "Tambah" menyimpan baris kosong ke DB
  // lalu dibuang lagi saat kumpul → nomor kedua mustahil dibuat.)
  const kumpul = (): WaKantor => {
    const out: WaKantor = {};
    host.querySelectorAll<HTMLElement>('.wk_rows').forEach((box) => {
      const c = box.dataset['c'] ?? '', p = box.dataset['p'] as PeranWa;
      box.querySelectorAll<HTMLElement>('.wk_baris').forEach((row) => {
        const wa = wa62(row.querySelector<HTMLInputElement>('.wk_no')?.value ?? '');
        if (!wa) return;                                        // baris kosong dibuang saat simpan
        out[c] ??= {}; out[c]![p] ??= [];
        out[c]![p]!.push({ label: (row.querySelector<HTMLInputElement>('.wk_lbl')?.value ?? '').trim(), wa });
      });
    });
    return out;
  };
  const wireHapus = (row: HTMLElement): void => {
    row.querySelector('.wk_hapus')?.addEventListener('click', () => {
      const box = row.parentElement; row.remove();
      if (box && !box.querySelector('.wk_baris')) {
        host.querySelector<HTMLElement>(`.wk_kosong[data-c="${box.dataset['c']}"][data-p="${box.dataset['p']}"]`)
          ?.style.removeProperty('display');
      }
      toast('Baris dihapus — tekan 💾 Simpan Nomor.');
    });
  };
  host.querySelectorAll<HTMLElement>('.wk_baris').forEach(wireHapus);
  // Baris hidup di DOM sampai ditekan Simpan → beri tahu sebelum pindah tab, jangan hilang diam-diam.
  wkKotor = () => JSON.stringify(kumpul()) !== JSON.stringify(waKantor());
  host.querySelectorAll<HTMLElement>('.wk_tambah').forEach((b) => b.addEventListener('click', () => {
    const c = b.dataset['c'] ?? '', p = b.dataset['p'] as PeranWa;
    const box = host.querySelector<HTMLElement>(`.wk_rows[data-c="${c}"][data-p="${p}"]`);
    const contoh = PERAN_WA.find((x) => x.k === p)?.contoh ?? '';
    if (!box) return;
    box.insertAdjacentHTML('beforeend', barisHTML(contoh, canEdit));
    const row = box.lastElementChild as HTMLElement | null;
    if (row) { wireHapus(row); row.querySelector<HTMLInputElement>('.wk_lbl')?.focus(); }
    host.querySelector<HTMLElement>(`.wk_kosong[data-c="${c}"][data-p="${p}"]`)?.style.setProperty('display', 'none');
  }));
  host.querySelector('#wk_save')?.addEventListener('click', async () => {
    try {
      // Basis = nilai SEGAR dari DB; hanya cabang+peran yang tampil di layar ini yang ditimpa,
      // supaya cabang yang tak disentuh (mis. diubah orang lain barusan) tidak ikut terhapus.
      const segar = await fetchSetting<WaKantor>('wa_kantor', {});
      const layar = kumpul();
      host.querySelectorAll<HTMLElement>('.wk_rows').forEach((box) => {
        const c = box.dataset['c'] ?? '', p = box.dataset['p'] as PeranWa;
        segar[c] ??= {}; segar[c]![p] = layar[c]?.[p] ?? [];
      });
      await saveWaKantor(segar); toast('✅ Nomor WA kantor disimpan'); refresh();
    } catch (e) { toast((e as Error).message); }
  });
}
